export interface PrintSizeValue {
  widthIn: number;
  heightIn: number;
  marginIn?: number | null;
  galleryWrapIn?: number | null;
  galleryWrapMirrorEdges?: boolean | null;
}

export interface PrintSizeEntry {
  orderItemId: string;
  initialWidthIn: number | null;
  initialHeightIn: number | null;
  initialMarginIn?: number | null;
  initialGalleryWrapIn?: number | null;
  initialGalleryWrapMirrorEdges?: boolean | null;
}

export const calculateCurrentDpi = ({
  pixelWidth,
  pixelHeight,
  widthIn,
  heightIn,
  marginIn,
}: {
  pixelWidth?: number | null;
  pixelHeight?: number | null;
  widthIn?: number | null;
  heightIn?: number | null;
  marginIn?: number | null;
}): { dpiX: number | null; dpiY: number | null } => {
  const effectiveMargin = (() => {
    if (!isPositiveNumber(marginIn)) {
      return 0;
    }

    const limits: number[] = [];
    if (isPositiveNumber(widthIn)) {
      limits.push(widthIn / 2);
    }
    if (isPositiveNumber(heightIn)) {
      limits.push(heightIn / 2);
    }

    if (limits.length === 0) {
      return 0;
    }

    const maxAllowedMargin = Math.min(...limits);
    if (!isPositiveNumber(maxAllowedMargin)) {
      return 0;
    }

    return Math.min(marginIn, maxAllowedMargin);
  })();

  const hasDimensions = isPositiveNumber(widthIn) && isPositiveNumber(heightIn);

  const scaleFactor = (() => {
    if (!hasDimensions) {
      return null;
    }

    const width = widthIn as number;
    const height = heightIn as number;
    const longSide = Math.max(width, height);
    if (!isPositiveNumber(longSide)) {
      return null;
    }

    const scaled = longSide - effectiveMargin * 2;
    if (!isPositiveNumber(scaled)) {
      return null;
    }

    return scaled / longSide;
  })();

  const printableWidthIn = hasDimensions && isPositiveNumber(scaleFactor)
    ? (widthIn as number) * (scaleFactor as number)
    : isPositiveNumber(widthIn) ? widthIn : null;
  const printableHeightIn = hasDimensions && isPositiveNumber(scaleFactor)
    ? (heightIn as number) * (scaleFactor as number)
    : isPositiveNumber(heightIn) ? heightIn : null;

  const dpiX = typeof pixelWidth === 'number'
    && isPositiveNumber(printableWidthIn)
    ? Math.round(pixelWidth / (printableWidthIn as number))
    : null;
  const dpiY = typeof pixelHeight === 'number'
    && isPositiveNumber(printableHeightIn)
    ? Math.round(pixelHeight / (printableHeightIn as number))
    : null;
  return { dpiX, dpiY };
};

export const formatPpi = (dpiX?: number | null, dpiY?: number | null): string => {
  const hasDpiX = isPositiveNumber(dpiX);
  const hasDpiY = isPositiveNumber(dpiY);

  if (!hasDpiX && !hasDpiY) {
    return '—';
  }

  if (hasDpiX && hasDpiY) {
    return dpiX === dpiY ? `${dpiX} PPI` : `${dpiX} × ${dpiY} PPI`;
  }

  return `${dpiX ?? dpiY} PPI`;
};

const isPositiveNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;

export const normalizePrintSizeValue = (value: Partial<PrintSizeValue> | undefined | null): PrintSizeValue | null => {
  if (!value) {
    return null;
  }
  const { widthIn, heightIn, marginIn } = value;
  if (!isPositiveNumber(widthIn) || !isPositiveNumber(heightIn)) {
    return null;
  }
  const normalized: PrintSizeValue = { widthIn, heightIn };
  if (isPositiveNumber(marginIn)) {
    normalized.marginIn = marginIn;
  }
  if (isPositiveNumber(value.galleryWrapIn)) {
    normalized.galleryWrapIn = value.galleryWrapIn;
    if (typeof value.galleryWrapMirrorEdges === 'boolean') {
      normalized.galleryWrapMirrorEdges = value.galleryWrapMirrorEdges;
    }
  }
  if (isPositiveNumber(normalized.marginIn) && isPositiveNumber(normalized.galleryWrapIn)) {
    return null;
  }
  return normalized;
};

interface ComputeChangesOptions {
  readonly entries: readonly PrintSizeEntry[];
  readonly drafts: ReadonlyMap<string, PrintSizeValue> | Record<string, PrintSizeValue | undefined | null>;
  readonly active?: { orderItemId: string; size: PrintSizeValue | null | undefined };
  readonly resolveCurrentSize?: (orderItemId: string) => {
    widthIn: number | null;
    heightIn: number | null;
    marginIn?: number | null;
    galleryWrapIn?: number | null;
    galleryWrapMirrorEdges?: boolean | null;
  } | null | undefined;
}

export const computePrintSizeChanges = ({
  entries,
  drafts,
  active,
  resolveCurrentSize,
}: ComputeChangesOptions): Array<{ orderItemId: string; size: PrintSizeValue }> => {
  const draftMap = drafts instanceof Map ? new Map(drafts) : new Map<string, PrintSizeValue>();

  if (!(drafts instanceof Map)) {
    Object.entries(drafts).forEach(([key, value]) => {
      const normalized = normalizePrintSizeValue(value ?? undefined);
      if (normalized) {
        draftMap.set(key, normalized);
      }
    });
  }

  if (active?.orderItemId) {
    const normalizedActive = normalizePrintSizeValue(active.size ?? undefined);
    if (normalizedActive) {
      draftMap.set(active.orderItemId, normalizedActive);
    }
  }

  if (draftMap.size === 0) {
    return [];
  }

  const result: Array<{ orderItemId: string; size: PrintSizeValue }> = [];

  entries.forEach((entry) => {
    const nextSize = draftMap.get(entry.orderItemId);
    if (!nextSize) {
      return;
    }

        const fallbackWidth = entry.initialWidthIn ?? null;
        const fallbackHeight = entry.initialHeightIn ?? null;
        const fallbackMargin = entry.initialMarginIn ?? null;
        const fallbackGalleryWrap = entry.initialGalleryWrapIn ?? null;
        const fallbackGalleryWrapMirrorEdges = typeof entry.initialGalleryWrapMirrorEdges === 'boolean'
          ? entry.initialGalleryWrapMirrorEdges
          : null;

        const current = resolveCurrentSize?.(entry.orderItemId) ?? {
          widthIn: fallbackWidth,
          heightIn: fallbackHeight,
          marginIn: fallbackMargin,
          galleryWrapIn: fallbackGalleryWrap,
          galleryWrapMirrorEdges: fallbackGalleryWrapMirrorEdges,
        };
        const currentWidth = typeof current?.widthIn === 'number' ? current.widthIn : fallbackWidth;
        const currentHeight = typeof current?.heightIn === 'number' ? current.heightIn : fallbackHeight;
        const currentMargin = typeof current?.marginIn === 'number' ? current.marginIn : fallbackMargin;
        const currentGalleryWrap = typeof current?.galleryWrapIn === 'number' ? current.galleryWrapIn : fallbackGalleryWrap;
        const currentGalleryWrapMirrorEdges = typeof current?.galleryWrapMirrorEdges === 'boolean'
          ? current.galleryWrapMirrorEdges
          : fallbackGalleryWrapMirrorEdges;
    const nextMargin = typeof nextSize.marginIn === 'number' ? nextSize.marginIn : null;
    const nextGalleryWrap = typeof nextSize.galleryWrapIn === 'number' ? nextSize.galleryWrapIn : null;
    const nextGalleryWrapMirrorEdges = nextGalleryWrap != null
      ? nextSize.galleryWrapMirrorEdges === true
      : null;

    if (
      currentWidth === nextSize.widthIn
      && currentHeight === nextSize.heightIn
      && currentMargin === nextMargin
      && currentGalleryWrap === nextGalleryWrap
      && currentGalleryWrapMirrorEdges === nextGalleryWrapMirrorEdges
    ) {
      return;
    }

    result.push({ orderItemId: entry.orderItemId, size: nextSize });
  });

  return result;
};
